import { faArrowLeft, faArrowRight } from '@fortawesome/pro-regular-svg-icons';
import type { JSX } from 'react';
import React from 'react';
import { useTranslation } from 'react-i18next';

import type { Campaign } from '@feathr/blackbox';
import { CampaignState } from '@feathr/blackbox';
import { ActionBar, Button, ButtonValid, Icon } from '@feathr/components';
import { flattenErrors } from '@feathr/hooks';
import type { Model } from '@feathr/rachis';

import type { ICampaignValidationErrors } from '../../CampaignSummary';
import SaveCampaignButton from '../SaveCampaignButton';

interface ICampaignWizardActionBarProps {
  campaign: Campaign;
  childModels: Model[];
  currentStep: number;
  getStepErrors: (step: number) => string[];
  grandchildModels: Model[];
  isLoading: boolean;
  lastStep: number;
  onNext: () => void;
  onPrev: () => void;
  /** When provided, intercepts publish clicks on the publish SaveCampaignButton. */
  onPublishClick?: () => void;
  postSave?: (campaign: Campaign, stateChanged?: boolean) => Promise<void>;
  preSave?: (campaign: Campaign) => Promise<void>;
  validate: () => ICampaignValidationErrors;
  additionalSaveProps?: {
    disabled?: boolean;
    [key: string]: boolean | undefined | string;
  };
  additionalPublishProps?: {
    disabled?: boolean;
    [key: string]: boolean | undefined | string;
  };
}

function CampaignWizardActionBar({
  campaign,
  childModels,
  currentStep,
  getStepErrors,
  grandchildModels,
  isLoading,
  lastStep,
  onNext,
  onPrev,
  onPublishClick,
  postSave,
  preSave,
  validate,
  additionalSaveProps = {},
  additionalPublishProps = {},
}: ICampaignWizardActionBarProps): JSX.Element {
  const { t } = useTranslation();

  const publishErrors = [...flattenErrors(validate())];
  const readonly = campaign.isReadOnly;
  const hasErrors = publishErrors.length > 0;

  const saveButton = (
    <SaveCampaignButton
      campaign={campaign}
      childModels={childModels}
      disabled={
        isLoading ||
        campaign.get('state', CampaignState.Draft) === CampaignState.Publishing ||
        campaign.isReadOnly ||
        undefined
      }
      grandchildModels={grandchildModels}
      key={'save'}
      name={'save_changes'}
      preSave={preSave}
      showIcon={true}
      type={'secondary'}
      {...additionalSaveProps}
    />
  );

  const publishButton = (
    <SaveCampaignButton
      campaign={campaign}
      childModels={childModels}
      disabled={campaign.isReadOnly || additionalPublishProps.disabled || undefined}
      grandchildModels={grandchildModels}
      key={'publish'}
      name={'publish_or_stop'}
      onPublishClick={onPublishClick}
      postSave={postSave}
      preSave={preSave}
      shouldChangeState={true}
      showIcon={true}
      type={'success'}
      validate={validate}
      {...(({ disabled, ...rest }) => rest)(additionalPublishProps)}
    />
  );

  return (
    <ActionBar
      left={
        <>
          <Button
            disabled={currentStep === 0 || isLoading}
            onClick={onPrev}
            prefix={<Icon icon={faArrowLeft} />}
          >
            {t('Go back')}
          </Button>
          {currentStep !== lastStep && (
            <ButtonValid
              disabled={currentStep === lastStep || isLoading || undefined}
              errors={getStepErrors(currentStep)}
              name={'next_step'}
              onClick={onNext}
              suffix={<Icon icon={faArrowRight} />}
              type={!readonly && hasErrors ? 'primary' : 'secondary'}
            >
              {t('Next')}
            </ButtonValid>
          )}
        </>
      }
      right={
        <>
          {saveButton}
          {!readonly && publishButton}
        </>
      }
      usePortal={true}
    />
  );
}

export default CampaignWizardActionBar;
